export type PaymentReleaseSubMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  paymentRequestStatus?: string;
  projectInvoiceId?: string;
};

export type PaymentReleaseMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  projectInvoiceId?: string;
  subMilestones?: PaymentReleaseSubMilestoneSelection[] | null;
};

export type PaymentReleaseRequestToSend =
  | { kind: "milestone"; milestoneId: string; name: string }
  | { kind: "sub_milestone_batch"; milestoneId: string; subMilestones: PaymentReleaseSubMilestoneSelection[] };

function isSubPaymentApproved(sub: PaymentReleaseSubMilestoneSelection): boolean {
  return String(sub.paymentRequestStatus ?? "").toLowerCase() === "approved";
}

function getUnreleasedSubs(milestone: PaymentReleaseMilestoneSelection): PaymentReleaseSubMilestoneSelection[] {
  return (milestone.subMilestones ?? []).filter((sub) => !isSubPaymentApproved(sub));
}

function isOnSameInvoiceAsMilestone(sub: PaymentReleaseSubMilestoneSelection, milestone: PaymentReleaseMilestoneSelection): boolean {
  return !sub.projectInvoiceId || sub.projectInvoiceId === milestone.projectInvoiceId;
}

/**
 * Builds payment-release API payloads from selected milestone/sub-milestone ids.
 * When the parent milestone is selected, its same-invoice subs are folded into a single
 * milestone request — the backend marks those subs pending too, so the client sees them
 * under that one request instead of getting a separate sub-milestone request. Subs on a
 * different invoice than the parent (e.g. a VO billed separately) always need their own
 * sub-milestone batch request, even when the parent is selected alongside them, since the
 * backend only auto-includes same-invoice subs in a milestone request.
 */
export function buildPaymentReleaseRequestsToSend(
  selectedItemIds: Iterable<string>,
  milestones: PaymentReleaseMilestoneSelection[]
): PaymentReleaseRequestToSend[] {
  const selected = new Set(selectedItemIds);
  const result: PaymentReleaseRequestToSend[] = [];

  for (const milestone of milestones) {
    const parentSelected = selected.has(milestone.id);
    const selectedSubs = (milestone.subMilestones ?? []).filter((sub) => selected.has(sub.id));

    if (selectedSubs.length > 0) {
      if (parentSelected) {
        result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });

        const crossInvoiceSubs = selectedSubs.filter((sub) => !isOnSameInvoiceAsMilestone(sub, milestone));
        if (crossInvoiceSubs.length > 0) {
          result.push({
            kind: "sub_milestone_batch",
            milestoneId: milestone.id,
            subMilestones: crossInvoiceSubs.map((sub) => ({ id: sub.id, name: sub.name, budgetAmount: sub.budgetAmount })),
          });
        }
        continue;
      }

      result.push({
        kind: "sub_milestone_batch",
        milestoneId: milestone.id,
        subMilestones: selectedSubs.map((sub) => ({ id: sub.id, name: sub.name, budgetAmount: sub.budgetAmount })),
      });
      continue;
    }

    if (parentSelected) {
      const unreleasedSubs = getUnreleasedSubs(milestone);
      if (unreleasedSubs.length > 0) {
        result.push({
          kind: "sub_milestone_batch",
          milestoneId: milestone.id,
          subMilestones: unreleasedSubs.map((sub) => ({
            id: sub.id,
            name: sub.name,
            budgetAmount: sub.budgetAmount,
            paymentRequestStatus: sub.paymentRequestStatus,
          })),
        });
        continue;
      }
      result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
    }
  }

  return result;
}
